<template>
    <contact-details :contactId="props.contactId" :groupId="props.groupId">
        <vx-expandable-card class="col-span-12 lg:col-span-10">
            <template v-slot:header>
                <div class="mb-4">
                    <font-awesome-icon widthAuto :icon="faMessages"></font-awesome-icon>
                    Messages
                </div>
            </template>
            <template v-slot:content>
                <div v-if="loading && !messages.length"
                    class="text-center py-10">
                    <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
                </div>

                <vx-alert v-else-if="!loading && messages.length === 0 && !error"
                    :closeable="false">
                    There are no messages for this contact.
                </vx-alert>

                <vx-alert v-else-if="error"
                    color="danger"
                    :closeable="false">
                    {{ error }}
                </vx-alert>

                <div v-else
                    class="bg-white border-solid border-slate-200 border py-6 rounded-xl flex flex-col gap-4">
                    <template v-for="(message, msgIndex) in messages"
                        :key="message.id">

                        <div class="px-6">
                            <message-cloud :message="message" :groupsMap="groupsMap" :isAggregatedView="isAggregatedView">
                            </message-cloud>
                        </div>

                        <div v-if="showDivider(message, msgIndex)"
                            class="my-6 relative flex justify-center"
                            data-test="message-divider">
                            <div
                                class="absolute inset-bs-1/2 -translate-y-1/2 h-px bg-slate-200 inset-s-0 w-full">
                            </div>
                            <div
                                class="relative bg-slate-50 border-slate-200 border border-solid rounded-full">
                                <div
                                    class="text-xs border-solid border-t border-x-0 border-b-0 border-t-white rounded-full py-1 px-3 text-slate-500">
                                    {{ weekDayOfMonth(message.created_at) }}
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
        </vx-expandable-card>
    </contact-details>
</template>

<script setup>
import ContactDetails from '../ContactDetails.vue';
import { VxIconLoading, VxAlert, VxExpandableCard } from '@voxie/frontend-components';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { ref, computed } from 'vue';
import { weekDayOfMonth } from '~/components/filters';
import { messagesClient, contactsClient } from '~/services';
import dayjs from '~/utils/dayjs';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faMessages } from '@fortawesome/pro-solid-svg-icons';
import MessageCloud from '~/components/messages/browser-extension/components/misc/MessageCloud.vue';

const props = defineProps({
    contactId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
    },
});

const teamId = Spark.state.currentTeam.id;

const error = ref();
const messages = ref([]);
const loading = ref(false);
const groups = ref([]);

const groupsMap = computed(() => new Map(groups.value.map((g) => [g.group_id, g])));
const isAggregatedView = computed(() => !props.groupId);

const loadGroups = async () => {
    if (props.groupId) {
        return;
    }
    try {
        const response = await contactsClient.listContactGroups(teamId, props.contactId);
        groups.value = response.data.data;
    } catch (e) {
        console.error('Failed to load groups', e);
    }
};

const loadMessages = () => {
    loading.value = true;

    const request = props.groupId
        ? messagesClient.groupListMessages(teamId, props.groupId, props.contactId, { sort_dir: 'desc' })
        : messagesClient.listMessages(teamId, props.contactId, { sort_dir: 'desc', include_group: 1 });

    request.then((response) => {
        messages.value = response.data.data;
    })
        .catch((e) => {
            console.error(e);
            if (
                !e?.response?.status ||
                e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR ||
                e?.response.status == HTTP_NOT_FOUND
            ) {
                error.value = 'There was an error communicating with the platform.';
            } else {
                error.value = e.response?.data?.message || 'Something went wrong.';
            }
        })
        .finally(() => {
            loading.value = false;
        });
};

const showDivider = (message, index) => {
    if (index === messages.value.length - 1) {
        return true
    }

    const nextMessage = messages.value[index + 1];

    return Boolean(nextMessage) && !dayjs(nextMessage.created_at).isSame(dayjs(message.created_at), 'day');
};

loadMessages();
loadGroups();
</script>
